import type {ClassData, ClassTable} from './catalog';
import type {SpellcastingSource} from './collect-effects';

export type SpellSlot = {
	/** ключ для учёта потраченных: круг или `pact` */
	key: string;
	/** круг ячейки */
	level: number;
	max: number;
	spent: number;
	/** ячейки магии договора */
	pact: boolean;
};

type CasterProgression = 'full' | 'half' | 'third' | 'pact';

type SpellcasterClass = {
	classData: ClassData;
	level: number;
};

const PACT_SLOTS_COLUMN = 'Ячейки заклинаний';
const PACT_SLOT_LEVEL_COLUMN = 'Круг ячейки';
const MAX_SPELL_LEVEL = 9;

const slotColumn = (spellLevel: number) => `@${spellLevel}`;

export const findFullCasterTable = (classes: ClassData[]) =>
	classes.find((classData) => classData.table.columns.includes(slotColumn(MAX_SPELL_LEVEL)))?.table;

export function calculateSpellSlots(
	classes: SpellcasterClass[],
	spellcasting: SpellcastingSource[],
	fullCasterTable: ClassTable | undefined,
	spentSlots: Record<string, number>,
): SpellSlot[] {
	const pactSlots = classes
		.filter(({classData}) => casterProgression(classData, spellcasting) === 'pact')
		.flatMap(({classData, level}) => pactSlot(classData.table, level, spentSlots));

	const slotCasters = classes.filter(({classData}) => {
		const progression = casterProgression(classData, spellcasting);
		return progression && progression !== 'pact';
	});
	const [singleCaster] = slotCasters;
	const singleCasterTable =
		slotCasters.length === 1 ? subClassSpellTable(singleCaster.classData, spellcasting) : undefined;
	if (singleCasterTable) return [...slotsFromTable(singleCasterTable, singleCaster.level, spentSlots), ...pactSlots];

	const casterLevel = spellcasterLevel(classes, spellcasting);
	const sharedSlots = casterLevel && fullCasterTable ? slotsFromTable(fullCasterTable, casterLevel, spentSlots) : [];

	return [...sharedSlots, ...pactSlots];
}

const subClassSpellTable = (classData: ClassData, spellcasting: SpellcastingSource[]) =>
	spellcasting.find((source) => source.classId === classData.id && source.fromSubClass && source.table)?.table;

function casterProgression(classData: ClassData, spellcasting: SpellcastingSource[]): CasterProgression | undefined {
	const {columns} = classData.table;
	if (columns.includes(PACT_SLOTS_COLUMN)) return 'pact';
	if (columns.includes(slotColumn(MAX_SPELL_LEVEL))) return 'full';
	if (columns.includes(slotColumn(1))) return 'half';
	if (spellcasting.some((source) => source.classId === classData.id && source.fromSubClass)) return 'third';
	return undefined;
}

function spellcasterLevel(classes: SpellcasterClass[], spellcasting: SpellcastingSource[]): number {
	const casters = classes
		.map(({classData, level}) => ({level, progression: casterProgression(classData, spellcasting)}))
		.filter(({progression}) => progression && progression !== 'pact');

	if (casters.length === 1) {
		const [{level, progression}] = casters;
		if (progression === 'half') return Math.ceil(level / 2);
		if (progression === 'third') return Math.ceil(level / 3);
		return level;
	}

	return casters.reduce((total, {level, progression}) => {
		if (progression === 'half') return total + Math.ceil(level / 2);
		if (progression === 'third') return total + Math.floor(level / 3);
		return total + level;
	}, 0);
}

function slotsFromTable(table: ClassTable, casterLevel: number, spentSlots: Record<string, number>): SpellSlot[] {
	const row = table.rows[Math.min(casterLevel, table.rows.length) - 1] || [];
	return Array.from({length: MAX_SPELL_LEVEL}, (_, index) => index + 1)
		.map((spellLevel) => ({
			spellLevel,
			max: parseInt(row[table.columns.indexOf(slotColumn(spellLevel))] || '', 10),
		}))
		.filter(({max}) => max > 0)
		.map(({spellLevel, max}) => ({
			key: String(spellLevel),
			level: spellLevel,
			max,
			spent: Math.min(max, spentSlots[spellLevel] || 0),
			pact: false,
		}));
}

function pactSlot(table: ClassTable, classLevel: number, spentSlots: Record<string, number>): SpellSlot[] {
	const row = table.rows[classLevel - 1] || [];
	const max = parseInt(row[table.columns.indexOf(PACT_SLOTS_COLUMN)] || '', 10);
	const level = parseInt(row[table.columns.indexOf(PACT_SLOT_LEVEL_COLUMN)] || '', 10);
	if (!(max > 0 && level > 0)) return [];
	return [{key: 'pact', level, max, spent: Math.min(max, spentSlots.pact || 0), pact: true}];
}

export function maxSpellLevel(
	{classData, level}: SpellcasterClass,
	spellcasting: SpellcastingSource[],
	fullCasterTable: ClassTable | undefined,
): number {
	const progression = casterProgression(classData, spellcasting);
	const ownTable = subClassSpellTable(classData, spellcasting);
	const slots = ownTable
		? slotsFromTable(ownTable, level, {})
		: availableSlots(progression, classData.table, level, fullCasterTable);
	return Math.max(0, ...slots.map((slot) => slot.level));
}

function availableSlots(
	progression: CasterProgression | undefined,
	classTable: ClassTable,
	classLevel: number,
	fullCasterTable: ClassTable | undefined,
): SpellSlot[] {
	if (progression === 'pact') return pactSlot(classTable, classLevel, {});
	if (progression === 'third')
		return fullCasterTable ? slotsFromTable(fullCasterTable, Math.ceil(classLevel / 3), {}) : [];
	if (progression) return slotsFromTable(classTable, classLevel, {});
	return [];
}
